import { RSAA } from 'redux-api-middleware';
import { get } from 'lodash';
import uuid from 'node-uuid';
import endpoints from '../../utils/endpoints';
import { getMandateRequest } from '../../helpers/apiRequestBuilder/';
import { getAccountById } from '../accounts/selectors';
import { CAPABILITIES } from '../../utils/accountUtils';
import { getChunkingInfo } from '../../states/api/chunkingActions';
import { submitMultiPartChunkingRequest } from '../api/submitMultiPartChunkingRequest';
import { MANDATE_PAGE_SIZE } from '../../config';
import { getAllActiveMandates, getMandateActiveFilter, getFilteredResults, getMandateTotals } from './selectors';
export const RESET_MANDATE_LIST = 'RESET_MANDATE_LIST';
export const MANDATE_ACTIVE_TAB = 'MANDATE_ACTIVE_TAB';
export const MANDATE_ACTIVE_FILTER = 'MANDATE_ACTIVE_FILTER';
export const GET_ALL_MANDATES_REQUEST = 'GET_ALL_MANDATES_REQUEST';
export const GET_ALL_MANDATES_SUCCESS = 'GET_ALL_MANDATES_SUCCESS';
export const GET_ALL_MANDATES_FAILURE = 'GET_ALL_MANDATES_FAILURE';
export const GET_MANDATES_MULTIPART_SUCCESS = 'GET_MANDATES_MULTIPART_SUCCESS';
export const VIEW_MANDATE = 'VIEW_MANDATE';
export const VIEW_ARCHIVE_MANDATE = 'VIEW_ARCHIVE_MANDATE';
export const VIEW_MANDATE_BACK = 'VIEW_MANDATE_BACK';
export const EDIT_MANDATE = 'EDIT_MANDATE';
export const SET_ACTIVE_SORT_ORDER = 'SET_ACTIVE_SORT_ORDER';
export const SET_ARCHIVE_SORT_ORDER = 'SET_ARCHIVE_SORT_ORDER';
export const SET_ACTIVE_PAGE = 'SET_ACTIVE_PAGE';
export const SET_ARCHIVE_PAGE = 'SET_ARCHIVE_PAGE';
export const SET_MANDATE_RECORD_TYPE = 'SET_MANDATE_RECORD_TYPE';
export const SET_MANDATE_TOTALS = 'SET_MANDATE_TOTALS';

export const handleActiveTabChange = (mandateActiveTab) => ({
	type: MANDATE_ACTIVE_TAB,
	mandateActiveTab,
});

export const handleViewMandate = (mandate) => ({
	type: VIEW_MANDATE,
	mandate,
});

export const setArchiveViewMandate = (mandate) => ({
	type: VIEW_ARCHIVE_MANDATE,
	mandate,
});

export const handleEditMandate = (mandateId) => ({
	type: EDIT_MANDATE,
	mandateId,
});
export const handleViewMandateBack = () => ({
	type: VIEW_MANDATE_BACK,
});

export const resetMandateList = () => ({
	type: RESET_MANDATE_LIST,
	meta: {
		allowedDuringApiCall: true,
	},
});

export const setActiveSortOrder = (sortOrder) => ({
	type: SET_ACTIVE_SORT_ORDER,
	sortOrder,
});

export const setArchiveSortOrder = (sortOrder) => ({
	type: SET_ARCHIVE_SORT_ORDER,
	sortOrder,
});

export const setActivePage = (pageNumber) => ({
	type: SET_ACTIVE_PAGE,
	pageNumber,
});

export const setArchivePage = (pageNumber) => ({
	type: SET_ARCHIVE_PAGE,
	pageNumber,
});

export const setMandateTotals = (totals) => ({
	type: SET_MANDATE_TOTALS,
	totals,
});

export const getMandatesMultipartRequest = ({ getMandateMultipartRequest, meta }, dispatch) => {
	const body = JSON.stringify({
		batch_list: getMandateMultipartRequest,
	});
	const successAction = {
		type: GET_ALL_MANDATES_SUCCESS,
		meta,
	};
	const endpoint = endpoints.multipart.uri;
	return dispatch({
		[RSAA]: {
			endpoint,
			method: 'POST',
			types: [GET_ALL_MANDATES_REQUEST, successAction, GET_ALL_MANDATES_FAILURE],
			body,
			headers: { 'x-bpi-version': endpoints.multipart.x_bpi_version },
		},
	});
};

export const checkForMandateRequestFailure = (apiResponse) => apiResponse.code !== 200;

export const getFromAccount = (state, accountId) => {
	const account = getAccountById(state, accountId) || {};
	return {
		accountId: account.id,
		accountNumber: account.accountNumber,
		sortCode: account.sortCode,
		displayName: account.displayName,
		accountHolder: get(account, 'metadata.account_name'),
		number: account.number,
		type: account.type,
	};
};
export const mandateWithFromAccount = (mandate, fromAccount) => ({
	...mandate,
	fromAccount,
});
export const getMandatesFromApiResponse = (apiResponses, meta, state) => {
	const initialResults = {
		standingOrders: [],
		bacs: [],
		directDebits: [],
		ifts: [],
		chaps: [],
	};

	return apiResponses.reduce((results, apiResponse) => {
		const { fromAccountId = '', type = '' } = get(meta, apiResponse.id, {});
		const fromAccount = getFromAccount(state, fromAccountId);
		switch (type) {
			case 'SO':
				return {
					...results,
					standingOrders: [
						...results.standingOrders,
						...get(apiResponse, 'body.mandates', []).map((mandate) => mandateWithFromAccount(mandate, fromAccount)),
					],
				};
			case 'DD':
				return {
					...results,
					directDebits: [
						...results.directDebits,
						...get(apiResponse, 'body.mandates', []).map((mandate) => mandateWithFromAccount(mandate, fromAccount)),
					],
				};
			default:
				return {
					...results,
					bacs: [
						...results.bacs,
						...get(apiResponse, 'body.bacs', []).map((mandate) => mandateWithFromAccount(mandate, fromAccount)),
					],
					ifts: [
						...results.ifts,
						...get(apiResponse, 'body.ift', []).map((mandate) => mandateWithFromAccount(mandate, fromAccount)),
					],
					chaps: [
						...results.chaps,
						...get(apiResponse, 'body.chaps', []).map((mandate) => mandateWithFromAccount(mandate, fromAccount)),
					],
				};
		}
	}, initialResults);
};
export const handleMandateMultipartSuccess = (mandatesFromApiReponse) => ({
	type: GET_MANDATES_MULTIPART_SUCCESS,
	mandates: mandatesFromApiReponse,
});
export const handleMandateMultipartResponse = (response) => (dispatch, getState) => {
	if (response.type === GET_ALL_MANDATES_SUCCESS) {
		const apiResponses = get(response, 'payload.apiResponses', []);
		if (apiResponses.some(checkForMandateRequestFailure)) {
			return dispatch({
				type: GET_ALL_MANDATES_FAILURE,
			});
		}
		const meta = get(response, 'meta');
		const mandatesFromApiReponse = getMandatesFromApiResponse(apiResponses, meta, getState());
		const { mandateActiveTotal, mandateArchiveTotal } = sumTotalMandates(mandatesFromApiReponse);
		const {
			mandateActivePageTotal: globalMandateActivePageTotal,
			mandateArchivePageTotal: globalMandateArchivePageTotal,
		} = getMandateTotals(getState());
		const mandateActivePageTotal = globalMandateActivePageTotal
			? globalMandateActivePageTotal
			: Math.ceil(mandateActiveTotal / MANDATE_PAGE_SIZE);
		const mandateArchivePageTotal = globalMandateArchivePageTotal
			? globalMandateArchivePageTotal
			: Math.ceil(mandateArchiveTotal / MANDATE_PAGE_SIZE);
		dispatch(
			setMandateTotals({ mandateActivePageTotal, mandateArchivePageTotal, mandateActiveTotal, mandateArchiveTotal })
		);
		return dispatch(handleMandateMultipartSuccess(mandatesFromApiReponse));
	}
	return response;
};
export const createMandateRequestForMultiPart = (
	accounts,
	bankId,
	mandateRecordType,
	mandateStartIndex,
	mandateMaxRecords
) => {
	const initial = { getMandateMultipartRequest: [], meta: {} };
	const results = accounts.reduce((results, account) => {
		const { getMandateMultipartRequest, meta } = results;
		const { actionsAvailable, id } = account;
		if (actionsAvailable['/account/mandates/so/read']) {
			const generatedStandingOrderId = uuid.v1();
			meta[generatedStandingOrderId] = { fromAccountId: id, type: 'SO' };
			const soRequest = getMandateRequest(bankId, 'default')(
				generatedStandingOrderId,
				id,
				endpoints.getStandingOrders,
				mandateRecordType,
				mandateStartIndex,
				mandateMaxRecords
			);
			getMandateMultipartRequest.push(soRequest);
		}
		if (actionsAvailable['/account/mandates/dd/read']) {
			const generatedDirectDebitId = uuid.v1();
			meta[generatedDirectDebitId] = { fromAccountId: id, type: 'DD' };
			const ddRequest = getMandateRequest(bankId, 'default')(
				generatedDirectDebitId,
				id,
				endpoints.getDirectDebits,
				mandateRecordType,
				mandateStartIndex,
				mandateMaxRecords
			);
			getMandateMultipartRequest.push(ddRequest);
		}
		if (
			actionsAvailable['/account/payments/bacscredit/out'] ||
			actionsAvailable['/account/payments/bacsdebit/out'] ||
			actionsAvailable[CAPABILITIES.IFT]
		) {
			const generatedId = uuid.v1();
			meta[generatedId] = { fromAccountId: id, type: 'BACS' };
			const endPoint = actionsAvailable['/account/transactions/read']
				? endpoints.getScheduledPayment
				: endpoints.getUsersScheduledPayment;
			const bacsMandateRequest = getMandateRequest(bankId, 'default')(
				generatedId,
				id,
				endPoint,
				mandateRecordType,
				mandateStartIndex,
				mandateMaxRecords
			);
			getMandateMultipartRequest.push(bacsMandateRequest);
		}
		return results;
	}, initial);
	return results;
};

export const filterAccounts = (accounts, accountIds) => accounts.filter((a) => accountIds.includes(a.id));

export const noAccountFilter = (accounts) => accounts;

export const getMandates = (filterFunction) => (accountIds) => (dispatch, getState) => {
	const {
		session: { bankId },
		accounts,
	} = getState();
	const filteredAccounts = filterFunction(accounts, accountIds);

	const mandateRequestsforMultiPart = createMandateRequestForMultiPart(filteredAccounts, bankId);
	dispatch(resetMandateList());
	return getMandatesMultipartRequest(mandateRequestsforMultiPart, dispatch).then((response) =>
		handleMandateMultipartResponse(response)(dispatch, getState)
	);
};

export const getMandatesChunked = (filterFunction) => (accountIds) => (dispatch, getState) => {
	const state = getState();
	const {
		session: { bankId },
		managePayments: { mandateRecordType, mandateStartIndex, mandateMaxRecords },
		accounts,
	} = state;
	dispatch(resetMandateList());

	const filteredAccounts = filterFunction(accounts, accountIds);

	return partitionMandates(
		filteredAccounts,
		bankId,
		mandateRecordType,
		mandateStartIndex,
		mandateMaxRecords,
		dispatch,
		getState,
		state
	);
};

export const partitionMandates = (
	filteredAccounts,
	bankId,
	mandateRecordType,
	mandateStartIndex,
	mandateMaxRecords,
	dispatch,
	getState,
	state
) => {
	const mandateRequestsforMultiPart = createMandateRequestForMultiPart(
		filteredAccounts,
		bankId,
		mandateRecordType,
		mandateStartIndex,
		mandateMaxRecords
	);

	const { getMandateMultipartRequest, meta } = mandateRequestsforMultiPart;

	const mandateRequestTypes = [GET_ALL_MANDATES_REQUEST, GET_ALL_MANDATES_SUCCESS, GET_ALL_MANDATES_FAILURE];

	const scheduledPaymentChunkingDetails = getChunkingInfo(getMandateMultipartRequest, state, 'SCHEDULED_PAYMENTS');
	const { isChunkingRequired, totalChunks, chunks, totalItems } = scheduledPaymentChunkingDetails;

	if (isChunkingRequired) {
		return dispatch(submitMultiPartChunkingRequest(mandateRequestTypes, totalChunks, chunks, totalItems)).then(
			(response) => {
				response.meta = meta;
				handleMandateMultipartResponse(response)(dispatch, getState);
			}
		);
	}

	return getMandatesMultipartRequest(mandateRequestsforMultiPart, dispatch).then((response) =>
		handleMandateMultipartResponse(response)(dispatch, getState)
	);
};

export const getAllMandates = getMandates(noAccountFilter);
export const getAllMandatesChunked = getMandatesChunked(noAccountFilter);
export const getSelectedMandates = getMandates(filterAccounts);

export const sumTotalMandates = (mandates) => {
	const keys = ['standingOrders', 'bacs', 'directDebits', 'ifts', 'chaps'];

	const mandateActiveTotal = keys.reduce(
		(mandateTotal, mandateKey) =>
			mandateTotal + get(mandates, mandateKey, []).filter((mandate) => mandate.is_active).length,
		0
	);

	const mandateArchiveTotal = keys.reduce(
		(mandateTotal, mandateKey) =>
			mandateTotal + get(mandates, mandateKey, []).filter((mandate) => !mandate.is_active).length,
		0
	);

	return { mandateActiveTotal, mandateArchiveTotal };
};

export const handleUpdateTotals = () => (dispatch, getState) => {
	const state = getState();
	const { mandateArchivePageTotal } = state.managePayments;
	const activeMandates = getAllActiveMandates(state);
	const mandateFilter = getMandateActiveFilter(state);
	const filteredActiveMandates = getFilteredResults(activeMandates, mandateFilter);
	const mandateActiveTotal = filteredActiveMandates.length;
	const mandateActivePageTotal = Math.ceil(mandateActiveTotal / MANDATE_PAGE_SIZE);
	return dispatch(setMandateTotals({ mandateActivePageTotal, mandateArchivePageTotal }));
};

export const handleManagePaymentFilterChange = (mandateActiveFilter) => (dispatch) => {
	dispatch({
		type: MANDATE_ACTIVE_FILTER,
		mandateActiveFilter,
	});
	return dispatch(handleUpdateTotals());
};
